import { useState } from 'react'

export type ViewMode = 'day' | 'year' | 'month'

interface UseYearMonthNavigationParams {
  /** Whether the two-step year → month → day flow is enabled (enableYearMonthFlow) */
  enabled: boolean
  selectsRange?: boolean
  minDate?: Date
  maxDate?: Date
  onChange?: (date: any, event?: any) => void
  onSelect?: (date: any, event?: any) => void
}

interface UseYearMonthNavigationResult {
  mode: ViewMode
  setMode: (mode: ViewMode) => void
  /**
   * The date the user is navigating towards through the year → month steps
   * in single-date mode (no value is emitted via onChange during this navigation)
   */
  navDate: Date | null
  handleChange: (date: any, event?: any) => void
  handleSelect: (date: any, event?: any) => void
}

export const useYearMonthNavigation = ({
  enabled,
  selectsRange,
  minDate,
  maxDate,
  onChange,
  onSelect,
}: UseYearMonthNavigationParams): UseYearMonthNavigationResult => {
  const [mode, setMode] = useState<ViewMode>('day')
  const [navDate, setNavDate] = useState<Date | null>(null)

  // Single-date mode: clicking a year/month is navigation, not value selection
  const useInternalNav = enabled && !selectsRange

  const clampToBounds = (d: Date): Date => {
    if (minDate && d < minDate) return minDate
    if (maxDate && d > maxDate) return maxDate
    return d
  }

  const handleChange = (date: any, event?: any) => {
    // On year/month steps in single-date mode, only navigate — do not emit a value
    if (useInternalNav && mode !== 'day') return
    onChange?.(date, event)
  }

  const handleSelect = (date: any, event?: any) => {
    if (enabled) {
      if (mode === 'year') {
        const yearDate = date as Date

        if (useInternalNav) {
          setNavDate(clampToBounds(yearDate))
        } else {
          if (minDate && yearDate < minDate) {
            onChange?.([minDate, null])
          } else if (maxDate && yearDate > maxDate) {
            onChange?.([maxDate, null])
          }
        }

        setMode('month')
        return
      }

      if (mode === 'month') {
        const selected = date as Date | null

        if (useInternalNav) {
          if (selected) setNavDate(clampToBounds(selected))
          setMode('day')
          return
        }

        setMode('day')
        if (selected) {
          if (minDate && selected < minDate) {
            onChange?.([minDate, null])
            return
          }
          if (maxDate && selected > maxDate) {
            onChange?.([maxDate, null])
            return
          }
        }
        return
      }
    }

    // Actual click on a day — navigation target is no longer needed
    if (useInternalNav) setNavDate(null)
    onSelect?.(date, event)
  }

  return { mode, setMode, navDate, handleChange, handleSelect }
}
